/**
 * Hand-written mirror of marketing.yaml v1.2.0 (M7-101, Wave 14 — owner
 * ruling 2026-09-07: admin-managed ad banners for the customer app; v1.2.0
 * (M7-180, Wave 22) adds the two customer HOME SECTIONS — toggle, titles,
 * artwork — on the same banner permission keys and image pipeline).
 *
 * Binding invariants mirrored from the broadcast/contract:
 *  - `kind` is `none | store | product`; `product` is RESERVED — the server
 *    rejects it in this wave, so the panel renders it disabled.
 *  - `target_merchant_id` is required iff `kind = store` (single-branch
 *    platform: a store IS a merchant).
 *  - `status` is COMPUTED server-side per row — never written by the panel:
 *    `served` = active AND schedule window contains now AND (kind = none OR
 *    the target store is visible to customers); otherwise one of
 *    `inactive | scheduled | expired | target_hidden`.
 *  - `image_url` is PUBLIC content (immutable, hash-keyed) — plain <img>.
 *  - Reorder is a FULL ordered id list (`PUT …/order`), idempotent.
 *  - Multipart on update travels as `POST` + `X-HTTP-Method-Override: PATCH`
 *    (PHP does not parse multipart PATCH bodies — contract info.description).
 */

/** The override header a multipart update MUST carry (marketing.yaml info.description). */
export const METHOD_OVERRIDE_HEADER = "X-HTTP-Method-Override";

import type { Ulid } from "./common";

export const BANNER_KINDS = ["none", "store", "product"] as const;
export type BannerKind = (typeof BANNER_KINDS)[number];

export const BANNER_STATUSES = ["served", "inactive", "scheduled", "expired", "target_hidden"] as const;
export type BannerStatus = (typeof BANNER_STATUSES)[number];

/** Upload rules the server enforces; the panel validates them as UX (§11.1). */
export const BANNER_IMAGE_MIMES = ["image/jpeg", "image/png", "image/webp"] as const;
export const BANNER_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const BANNER_IMAGE_MIN_WIDTH = 600;
export const BANNER_TITLE_MAX = 120;

export interface AdBanner {
  readonly id: Ulid;
  /** Internal label — never shown to customers. */
  readonly title: string;
  readonly kind: BannerKind;
  readonly target_merchant_id: Ulid | null;
  /** Target store display name in the request locale; null when kind ≠ store or unresolved. */
  readonly target_name?: string | null;
  /** Absolute URL of the public image route with the CURRENT hash — opaque, used verbatim. */
  readonly image_url: string;
  /** Lower-case hex sha1 of the stored bytes — the cache key the URL embeds. */
  readonly image_hash: string;
  readonly image_mime: string;
  readonly is_active: boolean;
  /** UTC, INCLUSIVE lower bound; null = open. */
  readonly starts_at: string | null;
  /** UTC, EXCLUSIVE upper bound; null = open. */
  readonly ends_at: string | null;
  /** Display position, 0 first — owned by `PUT …/order`, never written by create/update. */
  readonly sort_order: number;
  readonly status: BannerStatus;
  readonly created_by: Ulid | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** The JSON PATCH body (no image) — every field optional on the wire. */
export interface AdBannerWrite {
  readonly title?: string;
  readonly kind?: BannerKind;
  readonly target_merchant_id?: Ulid | null;
  readonly is_active?: boolean;
  readonly starts_at?: string | null;
  readonly ends_at?: string | null;
}

export interface AdBannerList {
  readonly data: ReadonlyArray<AdBanner>;
}

export interface BannerOrderWrite {
  readonly ids: ReadonlyArray<Ulid>;
}

/* ------------------------------------------------------------------ */
/* Home sections — marketing.yaml v1.2.0 (M7-180 / M7-182, Wave 22)     */
/* ------------------------------------------------------------------ */

/**
 * The fixed customer home sections, in FEED order. The set is the contract:
 * the panel never creates or deletes one — adding a section is a contract
 * bump plus a seeder row (`HomeSectionKey`).
 */
export const HOME_SECTION_KEYS = ["free_delivery", "discounts", "request_driver", "offers_banner"] as const;
export type HomeSectionKey = (typeof HOME_SECTION_KEYS)[number];

/**
 * Keys whose titles the app never renders (M7-209/M7-210, broadcast
 * 2026-09-29): `request_driver` is an image-only entry below the banner. The
 * wire still carries `title_ar`/`title_en` for it (seeded, unused); the panel
 * offers no title fields for these keys.
 */
export const HOME_SECTION_UNTITLED_KEYS: ReadonlyArray<HomeSectionKey> = ["request_driver", "offers_banner"];

export function homeSectionHasTitles(key: HomeSectionKey): boolean {
  return !HOME_SECTION_UNTITLED_KEYS.includes(key);
}

/** Titles render on ONE line over the artwork — the contract caps them at 60. */
export const HOME_SECTION_TITLE_MAX = 60;
/** Artwork: the banner byte rules (mimes, 2 MB) with the home-section minimum width. */
export const HOME_SECTION_IMAGE_MIN_WIDTH = 600;

/** The admin read model (`HomeSection`). */
export interface HomeSection {
  readonly key: HomeSectionKey;
  /** false ⇒ the app hides the whole section. */
  readonly enabled: boolean;
  readonly title_ar: string;
  readonly title_en: string;
  /** Absolute public URL with the CURRENT hash, or null when no artwork is set. Opaque. */
  readonly image_url: string | null;
  readonly image_hash: string | null;
  readonly image_mime: string | null;
  /** UTC; when the current artwork was set. */
  readonly image_updated_at: string | null;
  /** null until an operator first edits the seeded row. */
  readonly updated_by: Ulid | null;
  readonly updated_at: string;
}

export interface HomeSectionList {
  /** Always exactly the contract set, in contract order. */
  readonly sections: ReadonlyArray<HomeSection>;
}

/** The JSON PATCH body — every field optional; absent = unchanged. */
export interface HomeSectionUpdate {
  readonly enabled?: boolean;
  readonly title_ar?: string;
  readonly title_en?: string;
}

/** `details[].code` values the artwork route answers with on `field = "image"`. */
export const HOME_SECTION_IMAGE_DETAIL_CODES = ["required", "image", "mimes", "max", "dimensions"] as const;
export type HomeSectionImageDetailCode = (typeof HOME_SECTION_IMAGE_DETAIL_CODES)[number];

/* ------------------------------------------------------------------ */
/* Store collections — marketing.yaml v1.4.0 (M7-252 / M7-253)          */
/* ------------------------------------------------------------------ */

/**
 * Admin-managed STORE COLLECTIONS (owner request 2026-10-03): a row of
 * portrait artwork tiles on the customer home under one configurable
 * heading. Each tile is a collection — title AR/EN, artwork, an ORDERED list
 * of linked stores. Mirrors marketing.yaml 1.4.0 (`Collection*` /
 * `CollectionsHeading` schemas, board M7-252).
 *
 *  - Titles 1..60; the heading 0..60 each (empty = the app draws no heading).
 *  - `store_ids` is a FULL ordered replacement on PATCH, at most 50, every id
 *    a store the customer directory shows — otherwise `400 VALIDATION_FAILED`
 *    with `details[].field = "store_ids.{i}"`, code `not_visible` / `exists`.
 *  - Artwork: the banner byte rules (jpeg / png / webp, ≤ 2 MB) with a
 *    PORTRAIT minimum of 400 × 400 (rendered `cover`). No remove route.
 *  - Reorder is the full id set (`PUT …/order`), answered with the list.
 *  - Permissions: the banner keys, like home sections.
 */
export const COLLECTION_TITLE_MAX = 60;
export const COLLECTION_HEADING_MAX = 60;
export const COLLECTION_STORES_MAX = 50;
/** Minimum on BOTH sides — the tile is portrait and cropped to cover. */
export const COLLECTION_IMAGE_MIN_SIDE = 400;

export interface CollectionHeading {
  readonly title_ar: string;
  readonly title_en: string;
}

export interface Collection {
  readonly id: Ulid;
  readonly title_ar: string;
  readonly title_en: string;
  /** false ⇒ the app does not draw this tile. */
  readonly enabled: boolean;
  /** Display position, 0 first — owned by `PUT …/order`. */
  readonly sort_order: number;
  /** Absolute public URL with the CURRENT hash, or null when no artwork is set. Opaque. */
  readonly image_url: string | null;
  readonly image_hash: string | null;
  readonly image_mime: string | null;
  readonly image_updated_at: string | null;
  /** Ordered; the order the app lists the stores in. */
  readonly store_ids: ReadonlyArray<Ulid>;
  readonly store_count: number;
  readonly updated_by: Ulid | null;
  readonly updated_at: string;
}

export interface CollectionList {
  readonly heading: CollectionHeading;
  /** Ordered by `sort_order`. */
  readonly collections: ReadonlyArray<Collection>;
}

export interface CollectionCreate {
  readonly title_ar: string;
  readonly title_en: string;
  readonly enabled?: boolean;
  readonly store_ids?: ReadonlyArray<Ulid>;
}

/** The JSON PATCH body — every field optional; absent = unchanged. */
export interface CollectionUpdate {
  readonly title_ar?: string;
  readonly title_en?: string;
  readonly enabled?: boolean;
  readonly store_ids?: ReadonlyArray<Ulid>;
}

export interface CollectionOrderWrite {
  readonly ids: ReadonlyArray<Ulid>;
}

export interface CollectionOrderResult {
  readonly collections: ReadonlyArray<Collection>;
}

export interface CollectionHeadingResult {
  readonly heading: CollectionHeading;
}

/** `details[].code` values the linking rule answers with on `field = "store_ids.{i}"`. */
export const COLLECTION_STORE_DETAIL_CODES = ["not_visible", "exists"] as const;
export type CollectionStoreDetailCode = (typeof COLLECTION_STORE_DETAIL_CODES)[number];

/** The artwork route answers with the home-section codes on `field = "image"`. */
export type CollectionImageDetailCode = HomeSectionImageDetailCode;

/* ------------------------------------------------------------------ */
/* Category layouts — marketing.yaml v1.5.0 (M7-265 / M7-266)           */
/* ------------------------------------------------------------------ */

/**
 * Per main store category (owner ruling 2026-10-05) the admin manages the
 * SCREEN the customer app shows after entering it:
 *  - `label_mode` — whether the subcategory tiles draw the image alone or
 *    the image with its name;
 *  - `subcategories` — ordered portrait tiles (title AR/EN, artwork, an
 *    ORDERED list of linked stores); a tap filters the store list;
 *  - `slots` — injected content: a `section` (title + assigned stores) or a
 *    `banner` (image + optional target store), each at a `position`:
 *    0 = before the store list, N = after the Nth group of three stores.
 *    Slots sharing a position keep their own order (`sort_order`).
 *
 * Mirrors marketing.yaml 1.5.0 (reconciled 2026-10-05 once it landed). `kind`
 * is fixed at create. Store links follow the collection
 * rule (`store_ids.{i}` → `not_visible` / `exists`), the banner target
 * likewise on `target_store_id`. Artwork: a subcategory tile is PORTRAIT
 * (≥ 400 × 400, cover); a banner is ≥ 600 px wide; the banner route refuses
 * a section with `422 SLOT_KIND_MISMATCH`.
 */
export const LABEL_MODES = ["image_only", "image_with_name"] as const;
export type LabelMode = (typeof LABEL_MODES)[number];

export const SLOT_KINDS = ["section", "banner"] as const;
export type SlotKind = (typeof SLOT_KINDS)[number];

export const LAYOUT_TITLE_MAX = 60;
export const LAYOUT_STORES_MAX = 50;
/** Subcategory tile: minimum on BOTH sides — portrait, cropped to cover. */
export const SUBCATEGORY_IMAGE_MIN_SIDE = 400;
/** Banner slot: the banner minimum width. */
export const SLOT_BANNER_IMAGE_MIN_WIDTH = 600;
/** `Slot.position` bounds — the contract's `maximum: 10000`. */
export const SLOT_POSITION_MAX = 10000;

export interface Subcategory {
  readonly id: Ulid;
  readonly category_id: Ulid;
  readonly title_ar: string;
  readonly title_en: string;
  /** false ⇒ the app does not draw this tile. */
  readonly enabled: boolean;
  /** Display position, 0 first — owned by `PUT …/subcategories/order`. */
  readonly sort_order: number;
  readonly image_url: string | null;
  readonly image_hash: string | null;
  readonly image_mime: string | null;
  readonly image_updated_at: string | null;
  /** Ordered; the order the app lists the stores in. */
  readonly store_ids: ReadonlyArray<Ulid>;
  readonly store_count: number;
  readonly updated_by: Ulid | null;
  readonly updated_at: string;
}

export interface Slot {
  readonly id: Ulid;
  readonly category_id: Ulid;
  readonly kind: SlotKind;
  /** 0 = before the list; N = after the Nth group of three stores. */
  readonly position: number;
  readonly enabled: boolean;
  /** Order among the slots at the SAME position — owned by `PUT …/slots/order`. */
  readonly sort_order: number;
  readonly title_ar: string | null;
  readonly title_en: string | null;
  /** Section only (a banner carries an empty list). */
  readonly store_ids: ReadonlyArray<Ulid>;
  readonly store_count: number;
  /** Banner only. */
  readonly image_url: string | null;
  readonly image_hash: string | null;
  readonly image_mime: string | null;
  readonly image_updated_at: string | null;
  /** Banner only, optional. */
  readonly target_store_id: Ulid | null;
  readonly updated_by: Ulid | null;
  readonly updated_at: string;
}

export interface CategoryLayout {
  readonly category_id: Ulid;
  readonly label_mode: LabelMode;
  /** Ordered by `sort_order`. */
  readonly subcategories: ReadonlyArray<Subcategory>;
  /** Ordered by `position`, then `sort_order`. */
  readonly slots: ReadonlyArray<Slot>;
}

export interface CategoryLayoutUpdate {
  readonly label_mode: LabelMode;
}

export interface SubcategoryCreate {
  readonly title_ar: string;
  readonly title_en: string;
  readonly enabled?: boolean;
  readonly store_ids?: ReadonlyArray<Ulid>;
}

/** The JSON PATCH body — every field optional; absent = unchanged. */
export interface SubcategoryUpdate {
  readonly title_ar?: string;
  readonly title_en?: string;
  readonly enabled?: boolean;
  readonly store_ids?: ReadonlyArray<Ulid>;
}

export interface SlotCreate {
  readonly kind: SlotKind;
  readonly position: number;
  readonly enabled?: boolean;
  /** Required for a section. */
  readonly title_ar?: string;
  readonly title_en?: string;
  /** Section only. */
  readonly store_ids?: ReadonlyArray<Ulid>;
  /** Banner only, optional. */
  readonly target_store_id?: Ulid | null;
}

/** The JSON PATCH body — `kind` is immutable and never travels. */
export interface SlotUpdate {
  readonly position?: number;
  readonly enabled?: boolean;
  readonly title_ar?: string;
  readonly title_en?: string;
  readonly store_ids?: ReadonlyArray<Ulid>;
  readonly target_store_id?: Ulid | null;
}

export interface LayoutOrderWrite {
  readonly ids: ReadonlyArray<Ulid>;
}

export interface SubcategoryOrderResult {
  readonly subcategories: ReadonlyArray<Subcategory>;
}

export interface SlotOrderResult {
  readonly slots: ReadonlyArray<Slot>;
}

/** The store-link rule answers with the collection codes on `store_ids.{i}` and `target_store_id`. */
export type LayoutStoreDetailCode = CollectionStoreDetailCode;

/** The artwork routes answer with the home-section codes on `field = "image"`. */
export type LayoutImageDetailCode = HomeSectionImageDetailCode;

/* ------------------------------------------------------------------ */
/* Home layout — marketing.yaml v1.6.0 (M7-268 / M7-269, §14.4, §14.7)  */
/* ------------------------------------------------------------------ */

/**
 * The customer HOME screen's middle block as the panel arranges it (owner
 * ruling 2026-10-05): one ORDERED list of refs — the three built-ins the
 * app already draws (`free_delivery`, `discounts`, `collections`) and the
 * admin's own entries (`entry:<ULID>`) — plus the entries themselves:
 *  - `image_section`: a picture (≥ 600 px wide) + assigned stores — the
 *    «توصيل مجاني» strip shape;
 *  - `title_section`: a heading + assigned stores, no picture;
 *  - `banner_group`: several banners rotating every `rotate_seconds`, each a
 *    standalone picture or linked to one store.
 * `title_ar/en` are REQUIRED on every kind: the admin-facing name, drawn in
 * the app only for a title section and as the full-list heading. `kind` is
 * fixed at create. Built against the shape as broadcast for M7-268 — the
 * yaml wins on reconciliation.
 */
export const HOME_BUILTIN_REFS = ["free_delivery", "discounts", "collections"] as const;
export type HomeBuiltinRef = (typeof HOME_BUILTIN_REFS)[number];

export const HOME_ENTRY_KINDS = ["image_section", "title_section", "banner_group"] as const;
export type HomeEntryKind = (typeof HOME_ENTRY_KINDS)[number];

/** A custom entry's ref in `order`: `entry:` + its id. */
export const HOME_ENTRY_REF_PREFIX = "entry:";

export const HOME_TITLE_MAX = 60;
export const HOME_STORES_MAX = 50;
/** Every home picture — section image and banner alike — is ≥ 600 px wide. */
export const HOME_IMAGE_MIN_WIDTH = 600;
export const HOME_ROTATE_MIN = 3;
export const HOME_ROTATE_MAX = 30;
export const HOME_ROTATE_DEFAULT = 5;

export function isHomeBuiltinRef(ref: string): ref is HomeBuiltinRef {
  return (HOME_BUILTIN_REFS as ReadonlyArray<string>).includes(ref);
}

export function homeEntryRef(id: Ulid): string {
  return `${HOME_ENTRY_REF_PREFIX}${id}`;
}

/** The entry id a ref names, or null for a built-in / malformed ref. */
export function homeEntryIdOf(ref: string): Ulid | null {
  if (!ref.startsWith(HOME_ENTRY_REF_PREFIX)) return null;
  const id = ref.slice(HOME_ENTRY_REF_PREFIX.length);
  return id === "" ? null : id;
}

export interface HomeBanner {
  readonly id: Ulid;
  readonly entry_id: Ulid;
  /** false ⇒ the app skips this banner in the rotation. */
  readonly enabled: boolean;
  /** Position inside its group, 0 first — owned by `PUT …/banners/order`. */
  readonly sort_order: number;
  readonly image_url: string | null;
  readonly image_hash: string | null;
  readonly image_mime: string | null;
  readonly image_updated_at: string | null;
  /** The store a tap opens; null = a standalone picture. */
  readonly target_store_id: Ulid | null;
  readonly updated_at: string;
}

export interface HomeEntry {
  readonly id: Ulid;
  readonly kind: HomeEntryKind;
  /** Admin-facing name; the app draws it for a title section and as the full-list heading. */
  readonly title_ar: string;
  readonly title_en: string;
  /** false ⇒ the app does not draw this entry. */
  readonly enabled: boolean;
  /** Creation sequence — the default order when nothing was saved; the display order is `HomeLayout.order`. */
  readonly sort_order: number;
  /** Image section only. */
  readonly image_url: string | null;
  readonly image_hash: string | null;
  readonly image_mime: string | null;
  readonly image_updated_at: string | null;
  /** Sections only — ordered; a banner group carries an empty list. */
  readonly store_ids: ReadonlyArray<Ulid>;
  readonly store_count: number;
  /** Banner group only (3..30); null on a section. */
  readonly rotate_seconds: number | null;
  /** Banner group only, ordered by `sort_order`; a section carries an empty list. */
  readonly banners: ReadonlyArray<HomeBanner>;
  readonly updated_by: Ulid | null;
  readonly updated_at: string;
}

export interface HomeLayout {
  /** Every orderable ref exactly once, in display order. */
  readonly order: ReadonlyArray<string>;
  readonly entries: ReadonlyArray<HomeEntry>;
}

/** `PUT /admin/marketing/home-layout/order` — the FULL ref set (422 `HOME_ORDER_INCOMPLETE`). */
export interface HomeOrderWrite {
  readonly refs: ReadonlyArray<string>;
}

export interface HomeEntryCreate {
  readonly kind: HomeEntryKind;
  readonly title_ar: string;
  readonly title_en: string;
  readonly enabled?: boolean;
  /** Sections only. */
  readonly store_ids?: ReadonlyArray<Ulid>;
  /** Banner group only, 3..30 (default 5). */
  readonly rotate_seconds?: number;
}

/** The JSON PATCH body — `kind` is immutable and never travels. */
export interface HomeEntryUpdate {
  readonly title_ar?: string;
  readonly title_en?: string;
  readonly enabled?: boolean;
  readonly store_ids?: ReadonlyArray<Ulid>;
  readonly rotate_seconds?: number;
}

/** `PATCH /admin/marketing/home-banners/{bannerId}` — `target_store_id: null` clears the link. */
export interface HomeBannerUpdate {
  readonly enabled?: boolean;
  readonly target_store_id?: Ulid | null;
}

/** `PUT /admin/marketing/home-entries/{id}/banners/order` — the FULL id set (422 `HOME_BANNER_ORDER_INCOMPLETE`). */
export interface HomeBannerOrderWrite {
  readonly ids: ReadonlyArray<Ulid>;
}

/** Store links follow the collection rule; the banner target likewise on `target_store_id`. */
export type HomeStoreDetailCode = CollectionStoreDetailCode;

/** The picture routes answer with the home-section codes on `field = "image"`. */
export type HomeImageDetailCode = HomeSectionImageDetailCode;

/* ------------------------------------------------------------------ */
/* Pop-up ads — marketing.yaml v1.7.0 (M7-300 / M7-301; owner request    */
/* 2026-10-24: several image pop-ups like the welcome card, each with a  */
/* per-day frequency). Mirror of the Orchestrator's binding broadcast;  */
/* reconciled against the yaml once promotions-agent lands it.          */
/*                                                                      */
/*  - one permission, `marketing.popup.manage`, for the whole surface;  */
/*  - `frequency_per_day` 0..50, 0 = unlimited;                         */
/*  - the picture travels on its own route (`PUT …/{id}/image`,         */
/*    jpeg/png/webp, ≥ 600 × 600, ≤ 2 MB) once the row has an id;       */
/*  - `servable` + `servable_reason` are COMPUTED per read — never      */
/*    written by the panel; the target store is NOT part of it (a      */
/*    hidden target is served as a plain image, never dropped);         */
/*  - reorder is a FULL ordered id list (`PUT …/popups/reorder`).       */
/* ------------------------------------------------------------------ */

export const POPUP_TITLE_MAX = 60;
export const POPUP_FREQUENCY_MIN = 0;
export const POPUP_FREQUENCY_MAX = 50;
/** 0 = unlimited (the contract's sentinel, shown as «بلا حد»). */
export const POPUP_FREQUENCY_UNLIMITED = 0;
/** Both sides of the square pop-up picture. */
export const POPUP_IMAGE_MIN_SIDE = 600;

/**
 * Why a pop-up is not served right now, as the server names it. Mirrored so
 * the panel can word each one; a value outside this set still renders (the
 * server's token humanised), never blank and never a raw key.
 */
export const POPUP_SERVABLE_REASONS = ["disabled", "no_image", "scheduled", "expired"] as const;
export type PopupServableReason = (typeof POPUP_SERVABLE_REASONS)[number];

/**
 * The target as the row carries it. The broadcast names it `{id, name_ar,
 * name_en}`; the Marketing presenter in flight answers `{id, visible}`. The
 * panel reads whichever arrives: names when present, otherwise the id is
 * resolved through the merchant directory (never shown bare, M7-37a).
 */
export interface PopupTargetStore {
  readonly id: Ulid;
  readonly name_ar?: string;
  readonly name_en?: string;
  /** §7.1 customer-app visibility of the target, when the server states it. */
  readonly visible?: boolean;
}

export interface PopupAd {
  readonly id: Ulid;
  /** Internal label — never shown to customers. */
  readonly title: string;
  readonly enabled: boolean;
  /** Display position, 0 first — owned by `PUT …/reorder`, never written by create/update. */
  readonly sort_order: number;
  /** Shows per device per day; 0 = unlimited. */
  readonly frequency_per_day: number;
  readonly target_store_id: Ulid | null;
  /** The target by NAME, resolved server-side — null when untargeted. */
  readonly target_store: PopupTargetStore | null;
  /** Public, hash-keyed picture URL; null until a picture is set. */
  readonly image_url: string | null;
  readonly image_hash?: string | null;
  readonly image_mime?: string | null;
  readonly image_updated_at?: string | null;
  /** UTC, INCLUSIVE lower bound; null = open. */
  readonly starts_at: string | null;
  /** UTC, EXCLUSIVE upper bound; null = open. */
  readonly ends_at: string | null;
  readonly servable: boolean;
  readonly servable_reason: string | null;
  readonly created_by?: Ulid | null;
  readonly updated_by?: Ulid | null;
  readonly created_at?: string;
  readonly updated_at: string;
}

export interface PopupAdList {
  readonly data: ReadonlyArray<PopupAd>;
}

/** JSON create — the picture follows on its own route once the row has an id. */
export interface PopupAdCreate {
  readonly title: string;
  readonly enabled?: boolean;
  readonly frequency_per_day?: number;
  readonly target_store_id?: Ulid | null;
  readonly starts_at?: string | null;
  readonly ends_at?: string | null;
}

/** Present-keys-only PATCH; a cleared nullable travels as a real null. */
export interface PopupAdUpdate {
  readonly title?: string;
  readonly enabled?: boolean;
  readonly frequency_per_day?: number;
  readonly target_store_id?: Ulid | null;
  readonly starts_at?: string | null;
  readonly ends_at?: string | null;
}

export interface PopupOrderWrite {
  readonly ids: ReadonlyArray<Ulid>;
}

/** The `image` detail codes — the same vocabulary every marketing picture route answers with. */
export type PopupImageDetailCode = HomeSectionImageDetailCode;
